import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { SiteHeaderEn } from "@/components/SiteHeaderEn";
import { SiteFooterEn } from "@/components/SiteFooterEn";
import { PartnerForm } from "@/components/PartnerForm";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Partner Programme: 10% Commission on Shopify Projects";
const DESCRIPTION =
  "For SEO freelancers, consultants and studios: 10% commission on the net project fee, €399 to €1,890 per referred Shopify project. Also applies to development and implementation tasks.";
const URL = `${SITE}/en/partner-program`;

const steps = [
  {
    no: "01",
    title: "Introduce the client",
    text: "Tell me the store and the contact person, or introduce us by email. The only requirement is that the referral reaches me before the first contact.",
  },
  {
    no: "02",
    title: "I review the case",
    text: "The store gets a free first assessment. If the project isn't the right move right now, I say so, to your client and to you.",
  },
  {
    no: "03",
    title: "The project starts",
    text: "If it turns into a project, I work directly with the client. You stay informed but coordinate nothing and carry no delivery responsibility.",
  },
  {
    no: "04",
    title: "Commission is paid",
    text: "10% of the net project fee, no cap. Paid within 14 days of the client's payment reaching me, against your invoice.",
  },
];

const examples = [
  { label: "Sprint", order: "€3,990", fee: "€399" },
  { label: "Programme", order: "€9,900", fee: "€990" },
  { label: "Partnership", order: "€18,900", fee: "€1,890" },
];

const fitFor = [
  "SEO freelancers who deliver traffic but don't touch the store",
  "Ads specialists whose campaigns depend on the checkout rate",
  "Web and branding studios without their own Shopify team",
  "Consultants with e-commerce clients and open implementation tasks",
];

const rules = [
  "A referral is valid for 6 months from the introduction",
  "The commission applies to the client's first referred project",
  "Commission is paid only on paid invoices, no advances",
  "Applies to all Shopify projects, not just conversion work",
  "I don't pitch against you and I don't take over your services",
];

const faqs = [
  {
    q: "How much is the commission exactly?",
    a: "10% of the net project fee, with no cap. That's €399 on the €3,990 sprint, €990 on the €9,900 programme and €1,890 on the €18,900 partnership.",
  },
  {
    q: "Does the commission only apply to conversion work?",
    a: "No. It applies to all Shopify projects. If your client needs a Shopify developer or you have a specific task on your desk, just get in touch, the same 10% of the net project fee applies.",
  },
  {
    q: "When is it paid out?",
    a: "Within 14 days of the client's payment reaching me, against your invoice. With instalments, pro rata per instalment.",
  },
  {
    q: "Does the commission also apply to follow-up projects?",
    a: "No. The commission applies to the client's first referred project.",
  },
  {
    q: "Do I have to sign a contract?",
    a: "No. A short email with the store and contact person is enough as proof of the referral. On request I'll confirm the arrangement in writing.",
  },
  {
    q: "Will you take my client away?",
    a: "No. I work on Shopify, conversion, implementation, development. SEO, ads, content or retainers stay with you; I actively recommend you.",
  },
];


export const Route = createFileRoute("/en/partner-program")({
  component: PartnerProgramEn,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/partner-werden" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/partner-program" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/partner-werden" },
      { rel: "alternate", hrefLang: "de", href: `${SITE}/partner-werden` },
      { rel: "alternate", hrefLang: "en", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

function PartnerProgramEn() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeaderEn />
      <main>
        <section className="veil border-b border-border/60 py-14 md:py-20">
          <div className="mx-auto max-w-3xl px-5">
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Partner programme
            </p>
            <h1 className="mt-5 text-4xl font-bold leading-tight md:text-5xl">
              Refer Shopify projects 
              <br />
              earn up to <span className="text-signal">€1,890</span>.
            </h1>
            <p className="mt-6 leading-relaxed text-muted-foreground">
              You look after e-commerce clients but don't cover the work inside the store?
              Pass the project to me. If it turns into an engagement, you get 10% of the
              net project fee, no cap, €399 to €1,890 per project. It's not limited to
              conversion work: if your client needs a Shopify developer or you have a
              specific task on your desk, just get in touch. No contract, no exclusivity,
              no workload for you.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#refer"
                className="shadow-signal inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Refer a project
                <ArrowUpRight className="h-4 w-4" />

              </a>
              <Link
                to="/en/cases"
                className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold hover:border-primary"
              >
                See results
              </Link>
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">What you earn</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {examples.map((e) => (
                <div
                  key={e.label}
                  className="shadow-lift rounded-lg border border-border bg-card p-6"
                >
                  <p className="text-sm text-muted-foreground">{e.label}</p>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Project fee {e.order}
                  </p>
                  <p className="text-signal font-display mt-1 text-3xl font-bold">
                    {e.fee}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              10% of the net project fee for every package, no cap, including pure
              development and implementation tasks. The commission applies to the client's
              first referred project.

            </p>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">How it works</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-4">
              {steps.map((s) => (
                <div key={s.no} className="rounded-lg border border-border bg-card p-6">
                  <span className="text-signal font-display text-4xl font-bold">
                    {s.no}
                  </span>
                  <h3 className="mt-4 text-base font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {s.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 md:grid-cols-2">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">Who this fits</h2>
              <ul className="mt-6 space-y-3">
                {fitFor.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {f}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
                Your campaign or your ranking brings more visitors, but the store doesn't
                turn them into buyers. That's exactly where I come in. And if your client
                needs a Shopify developer for a specific task instead: get in touch, the
                same commission applies. You keep your client, your results look better,
                and you earn on the referral.

              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 md:p-8">
              <h3 className="text-lg font-semibold">The ground rules</h3>
              <ul className="mt-5 space-y-3">
                {rules.map((r) => (
                  <li key={r} className="flex items-start gap-2.5 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Frequently asked</h2>
            <div className="mt-8 space-y-5">
              {faqs.map((f) => (
                <div key={f.q} className="rounded-lg border border-border bg-card p-6">
                  <h3 className="text-base font-semibold">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="refer" className="veil border-t border-border/60 py-16 md:py-20">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 md:grid-cols-2 md:items-center">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">Refer a project</h2>
              <p className="mt-5 text-muted-foreground">
                Send me the client's store URL, your name and, if you have one, the
                specific task. I'll confirm the referral in writing and come back within 48
                hours with a straight first assessment. If it turns into an engagement, you
                get 10% of the net project fee.
              </p>

            </div>
            <PartnerForm source="en-partner-program" lang="en" />
          </div>
        </section>
      </main>
      <SiteFooterEn />
    </div>
  );
}
